import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  formatDate,
  formatDateTime,
  getEpisodeWindowStatus,
  statusBadgeClassName,
  toLocalDateKey,
} from "../lib/episodeStatus";
import { formatReasonCode } from "../lib/intakeForm";
import { Episode } from "../types/incapacity";
import GenericTable, { GenericTableColumn } from "./GenericTable";

type EpisodesTableProps = {
  episodes: Episode[];
  ariaLabel: string;
  emptyState: string;
};

/**
 * Read-only table of an employee's episodes, with a status badge and a link to
 * open each episode. Presentational only — the caller decides which episodes to
 * pass (e.g. ongoing vs. recent).
 */
function EpisodesTable({ episodes, ariaLabel, emptyState }: EpisodesTableProps) {
  const { t } = useTranslation();
  const todayKey = toLocalDateKey(new Date());

  const columns = useMemo<GenericTableColumn<Episode>[]>(
    () => [
      {
        id: "reportedAt",
        header: t("Reported at"),
        cell: (episode) => formatDateTime(episode.reportedAt),
      },
      {
        id: "startDate",
        header: t("Start date"),
        cell: (episode) => formatDate(episode.startDate),
      },
      {
        id: "estimatedReturnDate",
        header: t("Est. return"),
        cell: (episode) => formatDate(episode.estimatedReturnDate),
      },
      {
        id: "returnDate",
        header: t("Return date"),
        cell: (episode) => formatDate(episode.returnDate),
      },
      {
        id: "status",
        header: t("Status"),
        cell: (episode) => {
          const status = getEpisodeWindowStatus(episode, todayKey);

          return (
            <span className={`statusBadge ${statusBadgeClassName(status)}`}>
              {t(status)}
            </span>
          );
        },
      },
      {
        id: "reasonCode",
        header: t("Reason"),
        cell: (episode) => formatReasonCode(episode.reasonCode) || "-",
      },
      {
        id: "actions",
        header: t("Actions"),
        headerClassName: "actionsColumn",
        cellClassName: "actionsColumn",
        cell: (episode) => (
          <Link
            className="secondaryButton tableActionButton"
            to={`/episodes/${episode.id}`}
          >
            {t("Open")}
          </Link>
        ),
      },
    ],
    [t, todayKey],
  );

  return (
    <GenericTable
      ariaLabel={ariaLabel}
      columns={columns}
      rows={episodes}
      rowKey={(episode) => episode.id}
      emptyState={<p className="hintText sectionHint">{emptyState}</p>}
    />
  );
}

export default EpisodesTable;
